export interface ApiClientOptions extends RequestInit {
    body?: any;
}

export class ApiClient {
    static async request<T = any>(
        endpoint: string,
        options: ApiClientOptions = {}
    ): Promise<T> {
        const { body, headers, ...rest } = options;

        let response: any;

        const isFormData = body instanceof FormData;
        const requestBody = body ? (isFormData ? body as FormData : JSON.stringify(body)) : undefined;
        const contentType = isFormData ? undefined : { 'Content-Type': 'application/json' };

        try {
            response = await fetch(endpoint, {
                ...rest,
                headers: {
                    ...contentType,
                    ...headers,
                },
                body: requestBody
            });
        } catch (error) {
            throw new Error(handleApiError(error));
        }

        if (!response.ok) {
            let serverMsg: string | undefined;
            try {
                const data = await response.json();
                serverMsg = data?.message || data?.error || data?.errorMessage;
            } catch {
                // ignore
            }

            switch (response.status) {
                case 400:
                    throw new Error(serverMsg ?? "Bad request.");
                case 401:
                    throw new Error(serverMsg ?? "Unauthorized. Please log in.");
                case 404:
                    throw new Error(serverMsg ?? "Not found.");
                case 429: 
                    throw new Error('You are requesting way too frequently. Try again in a few seconds')
                case 500:
                    throw new Error("Server error. Please try again later.");
                default:
                    throw new Error(serverMsg ?? `Error ${response.status}`);
            }
        }

        try {
            const responseAsText = await response.text();

            if (!responseAsText) {
                return { data: undefined } as T;
            }

            return JSON.parse(responseAsText);
        } catch (error) {
            throw new Error(handleApiError(error));
        }
    }

    static get<T = any>(url: string, options?: ApiClientOptions) {
        return this.request<T>(url, { ...options, method: 'GET' });
    }

    static post<T = any>(url: string, body: any, options?: ApiClientOptions) {
        return this.request<T>(url, { ...options, method: 'POST', body });
    }

    static put<T = any>(url: string, body: any, options?: ApiClientOptions) {
        return this.request<T>(url, { ...options, method: 'PUT', body });
    }

    static delete<T = any>(url: string, options?: ApiClientOptions) {
        return this.request<T>(url, { ...options, method: 'DELETE' });
    }
}

function handleApiError(error: unknown, fallbackMessage = "Something went wrong. Please try again!") {
    if (typeof error === "object" && error !== null && "response" in error) {
        const err = error as any;

        const status = err.response?.status;
        const data = err.response?.data;

        // Customize known statuses
        if (status === 400) {
            return data?.message || "Bad request.";
        }

        if (status === 401) {
            return "Unauthorized. Please log in.";
        }

        if (status === 500) {
            return "Server error. Please try again later.";
        }

        return data?.message || fallbackMessage;
    } else {
        return fallbackMessage;
    }
}